import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { FaWhatsapp } from 'react-icons/fa';

const HeroSection = ({ onWhatsAppClick }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-[80vh] bg-gray-900 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-90"></div>
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(220, 20, 60, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 80%, rgba(255, 215, 0, 0.05) 0%, transparent 50%)`
      }}></div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row items-center justify-between min-h-[70vh] lg:min-h-[80vh]">
          {/* Left Content */}
          <div className={`lg:w-1/2 text-center lg:text-left transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Golden Crest Animation */}
            <div className="flex justify-center lg:justify-start mb-8">
              <div className="relative">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-royal border-4 border-white">
                  <img
                    src="/assets/images/logo.png" // Adjust path if needed
                    alt="Nitin Online Hub Logo"
                    className="h-20 w-20 object-contain"
                    style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full animate-pulse-golden flex items-center justify-center">
                  <Icon name="Star" size={16} color="white" />
                </div>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              <span className="text-gradient-royal">Nitin</span>
              <br />
              <span className="text-2xl sm:text-3xl lg:text-4xl text-gray-300">Online Hub</span>
            </h1>

            {/* Tagline */}
            <p className="text-xl sm:text-2xl text-primary font-accent mb-8">
              Where Gaming Meets Royalty
            </p>

            {/* Description */}
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Born from the vision of creating India's most trusted premium gaming platform, Nitin Online Hub combines royal heritage with cutting-edge technology to deliver an unparalleled gaming experience.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
              variant="default"
              size="lg"
              onClick={onWhatsAppClick}
              className="bg-gradient-royal hover:shadow-glow animate-pulse-golden hover:animate-none flex items-center gap-2"
            >
              <FaWhatsapp size={20} className="text-green-500" />
              Meet Your Gaming Advisor
            </Button>

              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                Discover Our Legacy
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 mt-12">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} color="var(--color-success)" />
                <span className="text-sm text-gray-400">Licensed & Secure</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={20} color="var(--color-primary)" />
                <span className="text-sm text-gray-400">50K+ Players</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={20} color="var(--color-accent)" />
                <span className="text-sm text-gray-400">Industry Leader</span>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className={`lg:w-1/2 mt-8 lg:mt-0 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <div className="w-full max-w-sm lg:max-w-md mx-auto">
                <Image
                  src="/assets/images/hero.jpg"
                  alt="Nitin Gaming Platform Royal Heritage"
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-2xl shadow-royal"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
                
                {/* Floating Stats */}
                <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                      <Icon name="TrendingUp" size={24} color="white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">₹2.5Cr+</p>
                      <p className="text-gray-300 text-sm">Payouts This Month</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                      <Icon name="Clock" size={24} color="white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">24/7</p>
                      <p className="text-gray-300 text-sm">WhatsApp Support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={32} color="var(--color-primary)" />
      </div>
    </section>
  );
};

export default HeroSection;